import React, {
  FormEventHandler,
  MouseEvent,
  useEffect,
  useRef,
  useState,
} from 'react'
import { RpcError } from 'grpc-web'
import { toast } from 'react-toastify'
import {
  FrameworkRequirementGroup,
  ListFrameworkRequirementGroupsRequest,
  SaveFrameworkRequirementGroupsRequest,
} from '@trustero/trustero-api-web/lib/model/framework_pb'
import { ModelPromiseClient } from '@trustero/trustero-api-web/lib/model/model_grpc_web_pb'
import { useAuthorizedGrpcClient } from 'src/adapter'
import {
  useHideModal,
  useIsShowModal,
  useModalState,
} from 'src/Modal/ModalStateContext'
import { getErrorMessage } from 'src/Utils/globalHelpers'
import {
  StandardButton,
  StandardButtonSize,
  StandardButtonVariant,
  DeleteButton,
} from 'src/components/Reusable/Buttons'
import { FlexAlign, FlexRow } from 'src/components/Reusable/Flex'
import { TextInput } from 'src/components/Reusable/Inputs/TextInput/TextInput'
import { useCustomFrameworkWizard } from 'src/pages/Frameworks/context/CustomFrameworkWizardContext'
import { FrameworkWizardGroup } from 'src/pages/Frameworks/frameworks.types'
import { ModalForm, ModalFormId } from '../..'
import {
  ButtonDiv,
  ServiceModalBody,
  ServiceRow,
  ServiceRowContainer,
} from '../../../../pages/Services/services.styles'

type GroupDraft = {
  modelId?: string
  name: string
  description: string
  nameError?: string
}

const createEmptyGroup = (): GroupDraft => ({ name: '', description: '' })

const groupsToDrafts = (g: FrameworkWizardGroup[]): GroupDraft[] =>
  g.length > 0
    ? g.map((x) => ({
        modelId: x.modelId,
        name: x.name,
        description: x.description ?? '',
      }))
    : [createEmptyGroup()]

const responseToWizardGroups = (
  list: FrameworkRequirementGroup[],
): FrameworkWizardGroup[] =>
  list.map((row) => ({
    modelId: row.getModelId(),
    name: row.getName(),
    description: row.getDescription(),
  }))

export const AddFrameworkGroupsModal = (): JSX.Element => {
  const show = useIsShowModal(ModalFormId.ADD_FRAMEWORK_GROUPS)
  const hide = useHideModal({ modalId: ModalFormId.ADD_FRAMEWORK_GROUPS })
  const { openModal } = useModalState()
  const modelClient = useAuthorizedGrpcClient(ModelPromiseClient)
  const { groups, setGroups, appendComplianceFrameworkModelId } =
    useCustomFrameworkWizard()
  const groupsRef = useRef(groups)
  groupsRef.current = groups
  const [drafts, setDrafts] = useState<GroupDraft[]>([createEmptyGroup()])
  const [isSaving, setIsSaving] = useState(false)
  const [isLoadingRemote, setIsLoadingRemote] = useState(false)
  const hasSavedGroups = groups.some((g) => g.name.trim().length > 0)

  useEffect(() => {
    if (!show) return
    if (appendComplianceFrameworkModelId) {
      return
    }
    setDrafts(groupsToDrafts(groups))
  }, [show, groups, appendComplianceFrameworkModelId])

  useEffect(() => {
    if (!show || !appendComplianceFrameworkModelId) return

    let cancelled = false
    setIsLoadingRemote(true)
    const loadGroups = async () => {
      try {
        const resp = await modelClient.listFrameworkRequirementGroups(
          new ListFrameworkRequirementGroupsRequest().setComplianceFrameworkModelId(
            appendComplianceFrameworkModelId,
          ),
        )
        if (cancelled) return
        const list = resp.getGroupsList()
        const nextWizard = responseToWizardGroups(list)
        setGroups(nextWizard)
        setDrafts(groupsToDrafts(nextWizard))
      } catch (err) {
        if (!cancelled) {
          const errorMessage = getErrorMessage(err as RpcError)
          toast.error(errorMessage.message)
          setDrafts(groupsToDrafts(groupsRef.current))
        }
      } finally {
        if (!cancelled) {
          setIsLoadingRemote(false)
        }
      }
    }
    loadGroups()

    return () => {
      cancelled = true
    }
  }, [show, appendComplianceFrameworkModelId, modelClient, setGroups])

  const closeAndReturn = () => {
    hide()
    openModal(ModalFormId.ADD_FRAMEWORK_REQUIREMENTS)
  }

  const updateDraft = (index: number, patch: Partial<GroupDraft>) => {
    setDrafts((prev) => {
      const next = [...prev]
      next[index] = { ...next[index], ...patch }
      return next
    })
  }

  const removeDraft = (index: number) => {
    setDrafts((prev) => {
      if (prev.length === 1) return [createEmptyGroup()]
      const next = [...prev]
      next.splice(index, 1)
      return next
    })
  }

  const addRow = (e: MouseEvent) => {
    e.preventDefault()
    setDrafts((prev) => [...prev, createEmptyGroup()])
  }

  const onSave: FormEventHandler = async (e) => {
    e.preventDefault()
    const seen = new Set<string>()
    const nextDrafts: GroupDraft[] = []
    let hasErr = false
    drafts.forEach((d) => {
      const trimmedName = d.name.trim()
      if (!trimmedName) {
        nextDrafts.push(d)
        return
      }
      if (seen.has(trimmedName.toLowerCase())) {
        nextDrafts.push({ ...d, nameError: 'Duplicate group name' })
        hasErr = true
        return
      }
      seen.add(trimmedName.toLowerCase())
      nextDrafts.push({
        ...d,
        name: trimmedName,
        description: d.description.trim(),
        nameError: undefined,
      })
    })
    if (hasErr) {
      setDrafts(nextDrafts)
      return
    }

    const savedRows = nextDrafts.filter((d) => d.name.trim().length > 0)

    setIsSaving(true)
    try {
      if (appendComplianceFrameworkModelId) {
        const saveReq = new SaveFrameworkRequirementGroupsRequest()
          .setComplianceFrameworkModelId(appendComplianceFrameworkModelId)
          .setGroupsList(
            savedRows.map((d) => {
              const row = new FrameworkRequirementGroup()
                .setName(d.name)
                .setDescription(d.description ?? '')
              if (d.modelId) {
                row.setModelId(d.modelId)
              }
              return row
            }),
          )
        const saveResp = await modelClient.saveFrameworkRequirementGroups(
          saveReq,
        )
        setGroups(responseToWizardGroups(saveResp.getGroupsList()))
      } else {
        setGroups(
          savedRows.map((d) => ({
            modelId: d.modelId,
            name: d.name,
            description: d.description ?? '',
          })),
        )
      }
      closeAndReturn()
    } catch (err) {
      const errorMessage = getErrorMessage(err as RpcError)
      toast.error(errorMessage.message)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <ModalForm
      show={show}
      hide={closeAndReturn}
      formId={ModalFormId.ADD_FRAMEWORK_GROUPS}
      size="lg"
      title={hasSavedGroups ? 'Manage groups' : 'Add groups'}
      description="Add framework requirement groups."
      onBack={closeAndReturn}
      hideCancel
      customSubmitButton={
        <StandardButton
          type="submit"
          form={ModalFormId.ADD_FRAMEWORK_GROUPS}
          variant={StandardButtonVariant.PRIMARY}
          buttonSize={StandardButtonSize.MEDIUM}
          disabled={isSaving || isLoadingRemote}
        >
          Save groups
        </StandardButton>
      }
    >
      <form id={ModalFormId.ADD_FRAMEWORK_GROUPS} onSubmit={onSave}>
        <ServiceModalBody>
          {drafts.map((draft, index) => (
            <ServiceRowContainer key={index}>
              <ServiceRow align={FlexAlign.CENTER} justify={FlexAlign.CENTER}>
                <TextInput
                  name={`framework-group-name-${index}`}
                  initVal={draft.name}
                  label=""
                  placeholder="Group name"
                  onChangeCb={(v) =>
                    updateDraft(index, { name: v, nameError: undefined })
                  }
                  errorMessage={draft.nameError}
                  hasError={!!draft.nameError}
                  showErrorMessage={!!draft.nameError}
                  allowEmptyVal
                />
                <TextInput
                  name={`framework-group-desc-${index}`}
                  initVal={draft.description}
                  label=""
                  placeholder="Group description (optional)"
                  onChangeCb={(v) => updateDraft(index, { description: v })}
                  allowEmptyVal
                />
                <ButtonDiv>
                  <DeleteButton onClick={() => removeDraft(index)} />
                </ButtonDiv>
              </ServiceRow>
            </ServiceRowContainer>
          ))}
          <FlexRow justify={FlexAlign.CENTER}>
            <StandardButton
              variant={StandardButtonVariant.SECONDARY}
              buttonSize={StandardButtonSize.MEDIUM}
              onClick={addRow}
              disabled={isSaving || isLoadingRemote}
            >
              Add Group Row
            </StandardButton>
          </FlexRow>
        </ServiceModalBody>
      </form>
    </ModalForm>
  )
}
